import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { ApiKeyDetails } from '@/types/user'; // Import the type
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { trpc } from '@/lib/trpc'; // Import trpc client
import { useTranslation } from 'react-i18next';

interface ViewApiKeyDetailsDialogProps {
  keyId: string | null;
  keyName: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return 'N/A';
  if (seconds === 0) return '0 seconds';
  // Basic formatting, can be improved
  if (seconds < 60) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) !== 1 ? 's' : ''}`;
  return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) !== 1 ? 's' : ''}`;
};

// Helper to format JSON
const formatJson = (data: unknown): string => {
    try {
        return JSON.stringify(data, null, 2);
    } catch (e) {
        return 'Invalid JSON';
    }
}

const DetailItem = ({ label, value, className }: { label: string; value: React.ReactNode, className?: string }) => (
  <div className={cn("grid grid-cols-3 gap-2 py-2 border-b border-border/50", className)}>
    <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
    <dd className="text-sm col-span-2">{value}</dd>
  </div>
);

export function ViewApiKeyDetailsDialog({
  keyId,
  keyName,
  isOpen,
  onOpenChange,
}: ViewApiKeyDetailsDialogProps) {
  const { t } = useTranslation();

  // Use the new tRPC query hook with explicit type annotation for data
  const { data: apiKeyDetails, isLoading, error, isError } = trpc.settings.getApiKeyDetails.useQuery<ApiKeyDetails | null>(
    { id: keyId! }, // Pass the id in the input object
    {
      enabled: isOpen && !!keyId, // Only fetch when dialog is open and keyId is valid
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      retry: false, // Optional: prevent retries on error
    }
  );

  const renderContent = () => {
    // 1. Handle Loading State
    if (isLoading) {
      return (
        <div className="space-y-3 py-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-2/3 col-span-2" />
            </div>
          ))}
        </div>
      );
    }

    // 2. Handle Error State
    if (isError) {
      return (
        <div className="text-destructive py-4">
          {t('settings.apiKeys.details.errorLoading')}: {error?.message || t('common.unknownError')}
        </div>
      );
    }

    if (apiKeyDetails === null || apiKeyDetails === undefined) {
      return <div className="py-4">{t('settings.apiKeys.details.noData')}</div>;
    }

    return (
      <dl className="space-y-0 divide-y-0">
        <DetailItem label={t('settings.apiKeys.details.id')} value={<code>{apiKeyDetails.id}</code>} />
        <DetailItem label={t('settings.apiKeys.details.name')} value={apiKeyDetails.name ?? 'N/A'} />
        <DetailItem label={t('settings.apiKeys.details.prefix')} value={<code>{apiKeyDetails.keyPrefix}</code>} />
        <DetailItem label={t('settings.apiKeys.details.userId')} value={<code>{apiKeyDetails.userId}</code>} />
        <DetailItem
          label={t('settings.apiKeys.details.status')}
          value={
            <Badge variant={!apiKeyDetails.expiresAt || new Date(apiKeyDetails.expiresAt) > new Date() ? 'default' : 'destructive'}>
              {!apiKeyDetails.expiresAt || new Date(apiKeyDetails.expiresAt) > new Date() ? t('settings.apiKeys.status.active') : t('settings.apiKeys.status.expired')}
            </Badge>
          }
        />
        <DetailItem
          label={t('settings.apiKeys.details.createdAt')}
          value={apiKeyDetails.createdAt ? format(new Date(apiKeyDetails.createdAt), 'PPpp') : 'N/A'}
        />
        <DetailItem
          label={t('settings.apiKeys.details.updatedAt')}
          value={'N/A'} // Placeholder as it's not in schema
        />
        <DetailItem
          label={t('settings.apiKeys.details.expiresAt')}
          value={apiKeyDetails.expiresAt ? format(new Date(apiKeyDetails.expiresAt), 'PPpp') : t('settings.apiKeys.details.neverExpires')}
        />
        <DetailItem
          label={t('settings.apiKeys.details.rateLimitEnabled')}
          value={'N/A'} // Placeholder as it's not in schema
        />
         <DetailItem
          label={t('settings.apiKeys.details.lastUsed')}
          value={apiKeyDetails.lastUsedAt ? format(new Date(apiKeyDetails.lastUsedAt), 'PPpp') : t('settings.apiKeys.details.neverUsed')}
        />
        <DetailItem label={t('settings.apiKeys.details.permissions')} value={'N/A'} className="border-b-0" />
        <DetailItem label={t('settings.apiKeys.details.metadata')} value={'N/A'} className="border-b-0" />
      </dl>
    );
  };

   return (
     <Dialog open={isOpen} onOpenChange={onOpenChange}>
       <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
         <DialogHeader>
           <DialogTitle>
             {t('settings.apiKeys.details.title', { keyName: keyName || '...' })}
           </DialogTitle>
         </DialogHeader>
         <div className="py-4">
           {renderContent()}
         </div>
       </DialogContent>
     </Dialog>
   );
}